import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

const positions = new Map<string, number>();
const accountSection = (pathname: string): boolean =>
  /^\/account(?:\/(donations|favorites|profile|preferences|security|privacy))?\/?$/.test(pathname);

export function RouteNavigation(): null {
  const location = useLocation();
  const navigation = useNavigationType();
  const previousLocation = useRef<{ key: string; pathname: string } | null>(null);
  useEffect(() => {
    const previous = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    return () => {
      history.scrollRestoration = previous;
    };
  }, []);
  useLayoutEffect(() => {
    const previous = previousLocation.current;
    const preserveAccountPosition =
      navigation !== NavigationType.Pop &&
      previous !== null &&
      accountSection(previous.pathname) &&
      accountSection(location.pathname) &&
      !location.hash &&
      window.matchMedia('(max-width: 840px), (max-width: 1024px) and (max-height: 500px)').matches;
    previousLocation.current = { key: location.key, pathname: location.pathname };
    let interacted = false;
    let focused: Element | null = null;
    const target = preserveAccountPosition
      ? (positions.get(previous.key) ?? window.scrollY)
      : navigation === NavigationType.Pop
        ? (positions.get(location.key) ?? 0)
        : 0;
    // Keep the last position before React replaces a longer page with shorter content.
    let recordedPosition = window.scrollY;
    const recordPosition = (): void => {
      recordedPosition = window.scrollY;
    };
    const stop = (): void => {
      // WebKit can update scrollY before delivering its scroll event. Capture it before navigation.
      recordPosition();
      interacted = true;
    };
    const apply = (): void => {
      if (interacted) return;
      const main = document.getElementById('main-content');
      if (!main) return;
      if (!preserveAccountPosition && focused !== main) {
        main.focus({ preventScroll: true });
        focused = main;
      }
      if (location.hash) {
        try {
          document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
        } catch {
          /* A malformed fragment must not break navigation. */
        }
      } else window.scrollTo({ top: target, behavior: 'instant' });
      recordPosition();
    };
    apply();
    const mutations = new MutationObserver(apply);
    mutations.observe(document.getElementById('root') ?? document.body, {
      childList: true,
      subtree: true,
    });
    const resize = new ResizeObserver(apply);
    resize.observe(document.body);
    window.addEventListener('scroll', recordPosition, { passive: true });
    for (const event of ['pointerdown', 'keydown', 'wheel', 'touchstart'])
      window.addEventListener(event, stop, { capture: true, passive: true });
    return () => {
      positions.set(location.key, recordedPosition);
      if (positions.size > 100) {
        const first = positions.keys().next().value;
        if (first !== undefined) positions.delete(first);
      }
      mutations.disconnect();
      resize.disconnect();
      window.removeEventListener('scroll', recordPosition);
      for (const event of ['pointerdown', 'keydown', 'wheel', 'touchstart'])
        window.removeEventListener(event, stop, { capture: true });
    };
  }, [location.key, location.hash, location.pathname, navigation]);
  return null;
}
